/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch } from 'react-redux';
import { callApi } from 'api/helpers/api.helpers';
import addressBookApi from '../../../api/addressBook.api';
import { PartyStatus } from '../PartyStatus';
import useDeleteConfirmation from '../../common/useDeleteConfirmation';
import { OpenMode } from './openMode';
import { EntityType, usePartiesService } from '../../../shared/submission/usePartiesService';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';
import { startApplicationLoading, stopApplicationLoading } from '../../../redux/features/application/application.slice';
import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';

const mapContactToForm = currentContact => ({
  id: currentContact.id,
  role: currentContact.partyType,
  status: currentContact.status,
  personalDetails: {
    title: currentContact.title,
    firstName: currentContact.firstName,
    lastName: currentContact.lastName,
    nationality: currentContact.nationality,
  },
  company: {
    company: currentContact.company,
    department: currentContact.department,
  },
  companyDetails: {
    company: currentContact.company,
    department: currentContact.department,
    placeOfBusiness: currentContact.placeOfBusiness,
  },
  contactDetails: {
    residenceAddress: {
      streetAddress: currentContact.address,
      postalCode: currentContact.postalCode,
      poBox: currentContact.poBox,
      city: currentContact.city,
      country: currentContact.country,
      region: currentContact.region,
    },
    telephone: currentContact.telephone,
    fax: currentContact.fax,
    email: currentContact.email,
  },
});

export default function useContactForm() {
  const dispatch = useDispatch();
  const { contactId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [errors, setErrors] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [partyResult, setPartyResult] = useState(null);
  const [contact, setContact] = useState({});
  const [apiContact, setApiContact] = useState({});
  const [contactRef, setContactRef] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [resultMessage, setResultMessage] = useState();
  const { getEntityTypeByRole } = usePartiesService();

  const onDeleteDialogOpened = () => {
    // not implemented yet
  };
  const onDeleteDialogClosed = () => {
    // not implemented yet
  };

  const onContactsDeleted = async (contactIds) => {
    await callApi(addressBookApi.deleteContacts)(contactIds);
    history.push('/addressbook');
  };

  const { deleteDialog } = useDeleteConfirmation(onDeleteDialogOpened, onDeleteDialogClosed, onContactsDeleted);

  let openMode;
  if (!contactId) {
    openMode = OpenMode.NEW;
  } else if (contactId && location.pathname.endsWith('/edit')) {
    openMode = OpenMode.EDIT;
  } else {
    openMode = OpenMode.VIEW;
  }

  const loadContact = useCallback(async () => {
    const loadedContact = await callApi(addressBookApi.get)(contactId);
    if (loadedContact) {
      const contactForm = mapContactToForm(loadedContact);
      setApiContact(loadedContact);
      setContact(contactForm);
      setContactRef(contactForm);
    }
  }, [contactId]);

  useEffect(() => {
    if (contactId) {
      loadContact();
    }
  }, [loadContact]);

  const addressbookValidationSchema = useConfiguration(ConfigResourceProps.ADDRESSBOOK_VALIDATION_SCHEMA);
  const addressbookUiSchema = useConfiguration(ConfigResourceProps.ADDRESSBOOK_UI_SCHEMA);

  const checkIfValidForm = (form) => {
    const contactInfo = form?.formData;
    if (form.errors?.length === 0) {
      if (contactInfo && contactInfo.role === EntityType.NATURAL_PERSON) {
        setIsValid(contactInfo.personalDetails?.lastName && contactInfo.personalDetails?.firstName);
      } else if (contactInfo && contactInfo.role === EntityType.LEGAL_ENTITY) {
        setIsValid(contactInfo.companyDetails?.company);
      }
    } else {
      setIsValid(false);
    }
  };

  const onChange = (form) => {
    checkIfValidForm(form);
    setContact(form.formData);
  };

  const saveContactAndManageDuplicates = async (contactToSave) => {
    dispatch(startApplicationLoading());
    const result = await callApi(addressBookApi.create)(contactToSave, true);
    dispatch(stopApplicationLoading());
    setPartyResult(result);
    if (result && result?.status === PartyStatus.CONFIRMED) {
      setResultMessage('messages.contact.added.success');
      setContact(mapContactToForm(result));
      setShowSuccessDialog(true);
    }
  };

  const mapFormToContact = (currentContact) => {
    return {
      id: currentContact?.id,
      partyType: getEntityTypeByRole(currentContact?.role),
      ...currentContact.personalDetails,
      company: currentContact.companyDetails ? currentContact.companyDetails?.company : currentContact.company?.company,
      department: currentContact.companyDetails
        ? currentContact.companyDetails?.department
        : currentContact.company?.department,
      placeOfBusiness: currentContact.companyDetails?.placeOfBusiness,
      address: currentContact.contactDetails?.residenceAddress?.streetAddress,
      postalCode: currentContact.contactDetails?.residenceAddress?.postalCode,
      poBox: currentContact.contactDetails?.residenceAddress?.poBox,
      city: currentContact.contactDetails?.residenceAddress?.city,
      country: currentContact.contactDetails?.residenceAddress?.country,
      region: currentContact.contactDetails?.residenceAddress?.region,
      telephone: currentContact.contactDetails?.telephone,
      fax: currentContact.contactDetails?.fax,
      email: currentContact.contactDetails?.email,
    };
  };

  const onSave = () => {
    const contactToSave = mapFormToContact(contact);
    setApiContact(contactToSave);
    saveContactAndManageDuplicates(contactToSave);
  };

  const onEdit = async () => {
    const contactToSave = mapFormToContact(contact);
    const res = await callApi(addressBookApi.update)(contactToSave, contactId);
    setShowSuccessDialog(true);
    if (res) {
      const contactForm = mapContactToForm(res);
      setApiContact(res);
      setContact(contactForm);
      setContactRef(contactForm);
    }

    history.push(`/addressbook/contact/${contactId}`);
  };

  const onCancel = () => {
    setApiContact(mapFormToContact(contactRef));
    setContact(contactRef);
    setPartyResult(null);
  };

  const onDelete = deleteDialog.open(apiContact);

  const resolveAllDuplicates = async (resolvedList) => {
    const response = await callApi(addressBookApi.resolvePartyDuplicates)(partyResult.id, resolvedList[0]);
    if (response?.party?.status === PartyStatus.CONFIRMED) {
      setResultMessage('messages.contact.added.success');
      setContact(mapContactToForm(response.party));
    } else {
      setResultMessage('messages.no.contact.added');
    }
    setShowSuccessDialog(true);
  };

  return {
    validationSchemaAddressBook: addressbookValidationSchema.resource,
    uiSchemaAddressBook: addressbookUiSchema.resource,
    contact,
    apiContact,
    contactId,
    errors,
    showSuccessDialog,
    partyResult,
    openMode,
    deleteDialog,
    setContact,
    setErrors,
    setShowSuccessDialog,
    setPartyResult,
    onChange,
    onSave,
    onEdit,
    onDelete,
    resolveAllDuplicates,
    isValid,
    onCancel,
    saveContactAndManageDuplicates,
    mapContactToForm,
    mapFormToContact,
    resultMessage,
  };
}
